/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import * as _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import DateUtils from 'src/utils/date';
import React, { Component, ReactComponentElement } from 'react';

// first-party
import MiniDropdown from '../../components/menus/MiniDropdown';
import { YearInput, Label } from '@americanexpress/dls-react';

// styles
import './MonthPicker.scss';

/**
 * Component for MonthPicker
 */
export default class MonthPicker extends Component<{
    date: LocalDate;
    whichDate: string;
    label?: string;
    handleChangeDate: (date: LocalDate, name: string) => void | Promise<void>;
    yearCount?: number;
    mode?: 'past-only' | 'future-only' | 'any';
    startYear?: number;
    endYear?: number;
    abbreviated?: boolean;
    className?: string;
    callback?: any;
    id?: string;
}> {
    public override render(): ReactComponentElement<any> {
        const date: LocalDate = DateUtils.dateify(this.props.date);
        const dateYear: number = date.year();

        const mode: 'past-only' | 'future-only' | 'any' = this.props.mode || 'any';

        // calc start year and number of years
        const defaultYearCount: number = this.props.yearCount || 40;
        const thisYear: number = LocalDate.now().year();
        const startYear: number =
            this.props.startYear ||
            (this.props.mode === 'past-only' || mode === 'any' ? thisYear - defaultYearCount : thisYear);
        const endYear: number =
            this.props.endYear ||
            (mode === 'past-only'
                ? thisYear
                : startYear + (mode === 'any' ? defaultYearCount * 2 + 1 : defaultYearCount));

        const years: number[] = [];
        for (let i = startYear; i <= endYear; i++) {
            years.push(i);
        }

        let options: JSX.Element;
        if (this.props.abbreviated) {
            options = (
                <React.Fragment>
                    <option key="jan" value={1}>
                        Jan
                    </option>
                    <option key="feb" value={2}>
                        Feb
                    </option>
                    <option key="mar" value={3}>
                        Mar
                    </option>
                    <option key="apr" value={4}>
                        Apr
                    </option>
                    <option key="may" value={5}>
                        May
                    </option>
                    <option key="jun" value={6}>
                        Jun
                    </option>
                    <option key="jul" value={7}>
                        Jul
                    </option>
                    <option key="aug" value={8}>
                        Aug
                    </option>
                    <option key="sep" value={9}>
                        Sep
                    </option>
                    <option key="oct" value={10}>
                        Oct
                    </option>
                    <option key="nov" value={11}>
                        Nov
                    </option>
                    <option key="dec" value={12}>
                        Dec
                    </option>
                </React.Fragment>
            );
        } else {
            options = (
                <React.Fragment>
                    <option key="jan" value={1}>
                        January
                    </option>
                    <option key="feb" value={2}>
                        February
                    </option>
                    <option key="mar" value={3}>
                        March
                    </option>
                    <option key="apr" value={4}>
                        April
                    </option>
                    <option key="may" value={5}>
                        May
                    </option>
                    <option key="jun" value={6}>
                        June
                    </option>
                    <option key="jul" value={7}>
                        July
                    </option>
                    <option key="aug" value={8}>
                        August
                    </option>
                    <option key="sep" value={9}>
                        September
                    </option>
                    <option key="oct" value={10}>
                        October
                    </option>
                    <option key="nov" value={11}>
                        November
                    </option>
                    <option key="dec" value={12}>
                        December
                    </option>
                </React.Fragment>
            );
        }

        const extraClass = this.props.abbreviated ? 'abbreviated' : '';
        const id = _.uniqueId('month-picker-');
        return (
            <div className={this.props.className + ' month-picker'} id={this.props.id}>
                {this.props.label ? (
                    <Label htmlFor={id} className="after12">
                        {this.props.label}
                    </Label>
                ) : null}
                <div className="month-picker-row" id={id}>
                    <div>
                        <Label>Month</Label>
                        <MiniDropdown
                            value={date ? date.monthValue() : undefined}
                            onChange={(e: any) => {
                                this.handleChangeMonth(e).catch(console.error);
                            }}
                            className={`select-month ${extraClass}`}
                        >
                            {options}
                        </MiniDropdown>
                    </div>
                    <YearInput
                        value={dateYear}
                        onChange={(e: any) => {
                            this.handleChangeYear(e).catch(console.error);
                        }}
                        className={`select-year ${extraClass}`}
                        status={dateYear <= thisYear + 90 && dateYear >= thisYear - 20 ? 'success' : 'error'}
                        required
                    />
                </div>
            </div>
        );
    }

    public async handleChangeMonth(e: any): Promise<void> {
        const startDate: LocalDate = this.props.date;
        const date: LocalDate = LocalDate.of(startDate.year(), e.target.value, 1);
        await this.props.handleChangeDate(date, this.props.whichDate);
        if (this.props.callback) {
            window.setTimeout(() => {
                this.props.callback();
            }, 50);
        }
    }

    public async handleChangeYear(e: any): Promise<void> {
        const startDate: LocalDate = this.props.date;
        if (!startDate) {
            console.error('failed to get startDate', e);
            return;
        }
        const date: LocalDate = LocalDate.of(e.target.value, startDate.monthValue(), 1);
        await this.props.handleChangeDate(date, this.props.whichDate);
        if (this.props.callback) {
            window.setTimeout(() => {
                this.props.callback();
            }, 50);
        }
    }
}
